import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import SimpleHeader from "components/Headers/SimpleHeader";
import EvidenceModal from "./components/EvidenceModal";
import constants from "constants";
import helpers from "utils/helpers";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  FormGroup,
  Label,
  Input,
  FormFeedback
} from "reactstrap";

const formatContext = (context, t) => {
  if (!context) return "";
  if (context.includes(".") && !context.includes(",")) {
    let translationKey = `page.userListings.rejectionReasons.${context}.title`;
    let translated = t(translationKey);
    if (translated && translated !== translationKey) return translated;
    translationKey = `page.users.details.rejectionReasons.${context}.title`;
    translated = t(translationKey);
    if (translated && translated !== translationKey) return translated;
  }
  if (context.includes(",")) {
    return context.split(",").map(token => {
      const cleanToken = token.trim();
      if (cleanToken.includes(".")) {
        let transKey = `page.userListings.rejectionReasons.${cleanToken}.title`;
        let translated = t(transKey);
        if (translated && translated !== transKey) return translated;
        transKey = `page.users.details.rejectionReasons.${cleanToken}.title`;
        translated = t(transKey);
        if (translated && translated !== transKey) return translated;
      }
      const transKey = `categories.moderation.context.${cleanToken}`;
      const translatedToken = t(transKey);
      if (translatedToken && translatedToken !== transKey) return translatedToken;
      return cleanToken.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    }).join(", ");
  }
  const transKey = `categories.moderation.context.${context}`;
  const translated = t(transKey);
  if (translated && translated !== transKey) return translated;
  return context.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

function AppealsDetailsPage() {
  const { appealId } = useParams();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [decision, setDecision] = useState("rejected");
  const [reviewNotes, setReviewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [selectedEvidenceUrl, setSelectedEvidenceUrl] = useState("");

  const getEvidenceUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const s3Root = constants.s3Bucket.replace(/\/assets$/, "");
    return url.startsWith("assets/") ? `${s3Root}/${url}` : `${constants.s3Bucket}/${url}`;
  };

  const fetchAppealDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await userServices.getAppealDetails(appealId);
      if (!res || !res.success) {
        setError(res?.error || t("page.flagged.toast.fetchError", "Failed to fetch appeal details."));
        return;
      }
      setAppeal(res?.data || null);
    } catch (err) {
      console.error(err);
      setError(t("page.flagged.toast.genericError", "Failed to load details."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppealDetails();
    // eslint-disable-next-line
  }, [appealId]);

  const handleSaveReview = async () => {
    if (!appeal || submitting) return;

    if (reviewNotes.trim().length < 20) {
      notify("error", t("categories.moderation.notesRequiredError", "Explanation notes must be at least 20 characters."));
      return;
    }

    setSubmitting(true);
    try {
      const res = await userServices.reviewAppeal(appeal._id, decision, reviewNotes.trim());
      if (res && res.success) {
        notify("success", t("categories.moderation.appealResolvedSuccess", "Appeal review saved successfully. User notified."));
        setReviewNotes("");
        fetchAppealDetails();
      } else {
        notify("error", res.error || t("page.flagged.toast.statusUpdateFailed", "Failed to save appeal review"));
      }
    } catch (err) {
      notify("error", t("page.flagged.toast.genericError", "An error occurred. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SimpleHeader name={t("sideNav.appealsQueue", "Appeals Queue")} parentName={t("sideNav.moderation", "Moderation")} />
        <div className="text-center py-5" style={{ minHeight: "300px", marginTop: "100px" }}>
          <Spinner color="primary" />
          <p className="mt-2 text-muted">{t("common.loading", "Loading appeal details...")}</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SimpleHeader name={t("sideNav.appealsQueue", "Appeals Queue")} parentName={t("sideNav.moderation", "Moderation")} />
        <Container className="mt-5">
          <Alert color="danger" className="p-4 shadow border-0">
            <h4 className="alert-heading">Error Loading Details</h4>
            <p>{error}</p>
            <hr />
            <Button color="secondary" size="sm" onClick={fetchAppealDetails}>
              {t("common.retry", "Retry")}
            </Button>
          </Alert>
        </Container>
      </>
    );
  }

  if (!appeal) return null;

  const userObj = appeal.userId || {};
  const flagObj = (appeal && typeof appeal.flagId === "object" && appeal.flagId) ? appeal.flagId : (appeal?.flagData || {});

  const isSuspensionAppeal = Boolean(
    appeal && (
      appeal.type === "suspension" ||
      !appeal.flagId ||
      flagObj.actionTaken === "suspend" ||
      flagObj.flagType === "suspension" ||
      (userObj.accountStatus === "suspended" && !flagObj.contentCategory)
    )
  );
  const userName = (userObj && `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim()) || t("categories.moderation.unknown", "Unknown User");
  const userInitials = userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "US";

  const isResolved = appeal.status === "accepted" || appeal.status === "rejected";

  const getStatusBadgeColor = (status) => {
    if (status === "accepted") return "#2dce89";
    if (status === "rejected") return "#f5365c";
    if (status === "underReview" || status === "under_review" || status === "pending") return "#fb6340";
    return "#8898aa";
  };

  const getSeverityBadgeColor = (severity) => {
    const map = {
      critical: { bg: "#f5365c", text: "#fff" },
      high: { bg: "#fb6340", text: "#fff" },
      medium: { bg: "#11cdef", text: "#fff" },
      low: { bg: "#e9ecef", text: "#525f7f" },
    };
    return map[severity] || map.low;
  };

  const statusColor = getStatusBadgeColor(appeal.status);

  return (
    <>
      <SimpleHeader name={t("sideNav.appealsQueue", "Appeals Queue")} parentName={t("sideNav.moderation", "Moderation")} />

      <Container className="mt--6" fluid>

        {/* ── Profile Header Card ── */}
        <Row className="mb--2">
          <Col xs="12">
            <Card className="shadow-sm border-0 rounded-lg">
              <CardBody className="py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle text-primary font-weight-bold mr-3"
                    style={{
                      width: "60px",
                      height: "60px",
                      backgroundColor: "#EFF6FF",
                      fontSize: "20px",
                      border: "1px solid #DBEAFE",
                      flexShrink: 0
                    }}
                  >
                    {userInitials}
                  </div>
                  <div>
                    <h2 className="mb-0 text-slate-800 font-weight-bold" style={{ fontSize: "20px" }}>{userName}</h2>
                    <span className="text-muted text-sm">{userObj.email || ""}</span>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Badge
                    className="badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm"
                    style={{
                      backgroundColor: statusColor + "18",
                      color: statusColor,
                      border: `1.5px solid ${statusColor}50`,
                      fontSize: "11px"
                    }}
                  >
                    {t("page.flagged.status." + appeal.status, appeal.status)}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Left Column */}
          <Col lg="8" xs="12">
            {/* User Appeal Card */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-comment mr-2 text-primary" />
                  {t("categories.moderation.appeals.userExplanation", "User Appeal")}
                </h4>
              </CardHeader>
              <CardBody className="p-4">
                <div
                  className="p-3"
                  style={{
                    backgroundColor: "#FFF9F5",
                    borderRadius: "8px",
                    borderLeft: "3px solid #fb6340",
                    fontSize: "14px",
                    color: "#4a5568",
                    lineHeight: 1.6,
                    fontStyle: "italic"
                  }}
                >
                  &ldquo;{appeal.appealText}&rdquo;
                </div>
                <div className="mt-3 text-muted text-xs font-weight-500">
                  {t("categories.moderation.appeals.submittedOn", "Submitted on")}: {helpers.getFormattedDate(appeal.submittedAt, t)}
                </div>

                {appeal.appealFiles && appeal.appealFiles.length > 0 && (
                  <>
                    <hr className="my-4" />
                    <h5 className="font-weight-bold heading-sm text-slate-800 mb-3">
                      <i className="fa fa-paperclip mr-2 text-primary" />
                      {t("categories.moderation.appeals.evidenceFiles", "Appeal Evidence Attachments")}
                    </h5>
                    <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
                      {appeal.appealFiles.map((file, index) => {
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
                        const fullUrl = getEvidenceUrl(file);
                        return (
                          <div key={index} className="d-flex flex-column align-items-center">
                            {isImage ? (
                              <div 
                                className="cursor-pointer hover:opacity-90 transition-opacity rounded-lg overflow-hidden border"
                                style={{ width: "120px", height: "90px", backgroundColor: "#f8f9fe" }}
                                onClick={() => {
                                  setSelectedEvidenceUrl(fullUrl);
                                  setEvidenceOpen(true);
                                }}
                              >
                                <img 
                                  src={fullUrl} 
                                  alt={`Evidence ${index + 1}`} 
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                                />
                              </div>
                            ) : (
                              <div 
                                className="cursor-pointer hover:bg-slate-100 transition-colors rounded-lg border d-flex flex-column align-items-center justify-content-center text-center text-muted"
                                style={{ width: "120px", height: "90px", backgroundColor: "#f8f9fe" }}
                                onClick={() => {
                                  setSelectedEvidenceUrl(fullUrl);
                                  setEvidenceOpen(true);
                                }}
                              >
                                <i className="fa fa-file-pdf text-danger" style={{ fontSize: "28px", marginBottom: "4px" }} />
                                <span className="text-xs font-weight-bold">
                                  {t("page.user-profile.appealModal.pdfFormat", "PDF File")}
                                </span>
                              </div>
                            )}
                            <small className="text-muted mt-1 text-xs">Attachment {index + 1}</small>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            {/* Original Violation / Suspension Info Card */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-exclamation-triangle mr-2 text-warning" />
                  {isSuspensionAppeal
                    ? t("page.user-profile.banner.accountSuspended", "Account Suspension")
                    : t("categories.moderation.appeals.originalDecision", "Original Flag Violation")}
                </h4>
              </CardHeader>
              <CardBody className="p-4">
                {isSuspensionAppeal ? (
                  <div>
                    <p className="text-sm text-slate-700 mb-2">
                      {t("categories.moderation.appeals.suspensionExplanation", "This user's account was suspended. They have submitted an appeal requesting reactivation.")}
                    </p>
                    {userObj.accountStatus && (
                      <Badge color={userObj.accountStatus === "suspended" ? "danger" : "success"} pill>
                        {t("categories.moderation.verification.account", "Account Status:")} {t("page.user-profile.status." + userObj.accountStatus, userObj.accountStatus)}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <>
                <Row className="mb-3">
                  <Col md="4" className="mb-3 mb-md-0">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      {t("table.headers.category", "Category")}
                    </span>
                    <span className="font-weight-bold text-slate-800 text-sm">
                      {t("page.flagged.categories." + flagObj.contentCategory, flagObj.contentCategory) || "—"}
                    </span>
                  </Col>
                  <Col md="4" className="mb-3 mb-md-0">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      {t("categories.moderation.flagType", "Flag Type")}
                    </span>
                    <span className="font-weight-bold text-slate-800 text-sm text-capitalize">
                      {flagObj.flagType || "—"}
                    </span>
                  </Col>
                  {flagObj.severity && (
                    <Col md="4">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("table.headers.severity", "Severity")}
                      </span>
                      <div>
                        <Badge
                          style={{
                            backgroundColor: getSeverityBadgeColor(flagObj.severity).bg,
                            color: getSeverityBadgeColor(flagObj.severity).text,
                            fontSize: "10px",
                            fontWeight: 700
                          }}
                          className="text-uppercase"
                        >
                          {flagObj.severity}
                        </Badge>
                      </div>
                    </Col>
                  )}
                </Row>
                <div>
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                    {t("categories.moderation.context", "Context")}
                  </span>
                  <div
                    className="p-3"
                    style={{
                      backgroundColor: "#F8FAFC",
                      borderRadius: "8px",
                      border: "1px dashed #E2E8F0"
                    }}
                  >
                    <p className="mb-0 text-sm text-slate-700 font-weight-500" style={{ lineHeight: 1.6 }}>
                      {formatContext(flagObj.context, t) || t("categories.moderation.noContext", "No specific context was logged.")}
                    </p>
                  </div>
                </div>
                </>
                )}
              </CardBody>
            </Card>

            {/* Resolution Details (for resolved appeals) */}
            {isResolved && appeal.reviewedBy && (
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-check-circle mr-2 text-success" />
                    {t("categories.moderation.appeals.resolutionDetails", "Resolution Details")}
                  </h4>
                </CardHeader>
                <CardBody className="p-4">
                  <Row className="mb-3">
                    <Col md="6" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("categories.moderation.appeals.resolutionDecision", "Decision")}
                      </span>
                      <div>
                        <Badge color={appeal.status === "accepted" ? "success" : "danger"} className="badge-pill px-3 py-1">
                          {appeal.status === "accepted" ? t("categories.moderation.appeals.actions.accept", "Accepted") : t("categories.moderation.appeals.actions.reject", "Rejected")}
                        </Badge>
                      </div>
                    </Col>
                    <Col md="6">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("categories.moderation.appeals.reviewedBy", "Reviewed By")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm d-block">
                        {appeal.reviewedBy.firstName} {appeal.reviewedBy.lastName}
                      </span>
                      <small className="text-muted">{appeal.reviewedBy.email}</small>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col xs="12">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("categories.moderation.appeals.reviewedAt", "Reviewed At")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">
                        {helpers.getFormattedDate(appeal.reviewedAt, t)}
                      </span>
                    </Col>
                  </Row>
                  {appeal.notes && (
                    <div>
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                        {t("categories.moderation.adminNote", "Admin Note")}
                      </span>
                      <div className="p-3 border rounded text-sm text-slate-700" style={{ lineHeight: 1.6 }}>
                        {appeal.notes}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </Col>

          {/* Right Column */}
          <Col lg="4" xs="12">
            {/* Adjudication Card */}
            {!isResolved && (
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-gavel mr-2 text-primary" />
                    {t("categories.moderation.appeals.resolutionForm", "Appeal Decision")}
                  </h4>
                </CardHeader>
                <CardBody className="p-4">
                  <FormGroup>
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                      {t("categories.moderation.chooseAction", "Choose Action")}
                    </span>
                    <div className="custom-controls-stacked">
                      <div className="custom-control custom-radio mb-3">
                        <Input
                          type="radio"
                          id="appeal-decision-accept"
                          name="appeal-decision"
                          className="custom-control-input"
                          checked={decision === "accepted"}
                          onChange={() => setDecision("accepted")}
                        />
                        <Label
                          className="custom-control-label font-weight-600 text-success text-sm"
                          for="appeal-decision-accept"
                          style={{ cursor: "pointer" }}
                        >
                          {t("categories.moderation.appeals.actions.accept", "Accept Appeal")}
                        </Label>
                      </div>
                      <div className="custom-control custom-radio mb-3">
                        <Input
                          type="radio"
                          id="appeal-decision-reject"
                          name="appeal-decision"
                          className="custom-control-input"
                          checked={decision === "rejected"}
                          onChange={() => setDecision("rejected")}
                        />
                        <Label
                          className="custom-control-label font-weight-600 text-danger text-sm"
                          for="appeal-decision-reject"
                          style={{ cursor: "pointer" }}
                        >
                          {t("categories.moderation.appeals.actions.reject", "Uphold Violation")}
                        </Label>
                      </div>
                    </div>
                  </FormGroup>

                  <FormGroup className="mt-4 mb-3">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                      {t("categories.moderation.appeals.explanationLabel", "Explanation Notes")}
                    </span>
                    <Input
                      type="textarea"
                      id="appeal-notes"
                      rows="4"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      invalid={reviewNotes.length > 0 && reviewNotes.length < 20}
                      placeholder={t("categories.moderation.appeals.notesPlaceholder", "Explain the reason for this decision (min 20 characters)...")}
                      className="border rounded-lg p-2 text-sm"
                    />
                    <FormFeedback>
                      {t("categories.moderation.notesValidation", "Decision explanation notes must be at least 20 characters.")}
                    </FormFeedback>
                    <div className="text-right text-xs text-muted mt-1">
                      {reviewNotes.length} / 20
                    </div>
                  </FormGroup>

                  <Button
                    color="primary"
                    className="w-100 font-weight-700 py-2"
                    onClick={handleSaveReview}
                    disabled={reviewNotes.length < 20 || submitting}
                  >
                    {submitting ? <Spinner size="sm" /> : t("categories.moderation.appeals.saveReview", "Save Adjudication")}
                  </Button>
                </CardBody>
              </Card>
            )}

            {/* Quick Info Card */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-info-circle mr-2 text-primary" />
                  {t("categories.moderation.quickInfo", "Quick Info")}
                </h4>
              </CardHeader>
              <CardBody className="p-4">
                <div className="mb-3">
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                    {t("table.headers.status", "Status")}
                  </span>
                  <div
                    className="d-flex align-items-center p-3 rounded"
                    style={{
                      backgroundColor: statusColor + "10",
                      border: `1.5px solid ${statusColor}35`
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: statusColor,
                        marginRight: "8px",
                        flexShrink: 0
                      }}
                    />
                    <span className="text-sm font-weight-bold text-slate-800 text-capitalize">{appeal.status}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                    {t("table.headers.submittedAt", "Submitted At")}
                  </span>
                  <span className="font-weight-bold text-slate-800 text-sm">
                    {helpers.getFormattedDate(appeal.submittedAt, t)}
                  </span>
                </div>
                <div>
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                    {t("table.headers.updatedAt", "Last Updated")}
                  </span>
                  <span className="font-weight-bold text-slate-800 text-sm">
                    {helpers.getFormattedDate(appeal.updatedAt, t)}
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {evidenceOpen && (
        <EvidenceModal
          isOpen={evidenceOpen}
          toggle={() => setEvidenceOpen(false)}
          fileUrl={selectedEvidenceUrl}
          uploadedAt={appeal.submittedAt}
        />
      )}
    </>
  );
}

export default AppealsDetailsPage;
